import { useState } from "react";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import { User, Mail, Lock, Eye, EyeOff, Save, KeyRound } from "lucide-react";

export default function AdminProfile() {
  const { user, token, login } = useAuth();

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingProfile(true);
    try {
      const response = await api.updateProfile(profileData, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success("Profile updated successfully");
        // Refresh user in auth context by re-fetching
        window.location.reload();
      }
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPassword(true);
    try {
      const response = await api.changePassword(passwordData, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success("Password changed successfully");
        setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      }
    } catch {
      toast.error("Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const roleLabel = (role) => {
    const map = { super_admin: "Super Admin", planner: "Planner", viewer: "Viewer" };
    return map[role] || role;
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-2xl" data-testid="admin-profile">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-white">My Profile</h1>
          <p className="text-gray-400 mt-1">Manage your account details and password</p>
        </div>

        {/* Avatar + Role */}
        <div className="flex items-center gap-5 mb-8 p-6 bg-white/5 border border-white/10 rounded-xl">
          <div className="w-16 h-16 rounded-full bg-[#c4646a]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[#c4646a] text-2xl font-medium">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-white text-lg font-medium">{user?.name}</p>
            <p className="text-gray-400 text-sm">{user?.email}</p>
            <span className="inline-block mt-1 px-3 py-0.5 bg-[#c4646a]/20 text-[#c4646a] rounded-full text-xs">
              {roleLabel(user?.role)}
            </span>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white/5 border border-white/10 rounded-xl mb-6">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#c4646a]/20 flex items-center justify-center">
              <User className="w-4 h-4 text-[#c4646a]" />
            </div>
            <h2 className="text-lg font-medium text-white">Personal Information</h2>
          </div>
          <form onSubmit={handleProfileSave} className="p-6 space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="luxury-input pl-12"
                  placeholder="Your full name"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="luxury-input pl-12"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={savingProfile}
                className="flex items-center gap-2 px-6 py-3 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {savingProfile ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white/5 border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#c4646a]/20 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#c4646a]" />
            </div>
            <h2 className="text-lg font-medium text-white">Change Password</h2>
          </div>
          <form onSubmit={handlePasswordSave} className="p-6 space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <input
                  type={showCurrent ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="luxury-input pl-12 pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <input
                  type={showNew ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="luxury-input pl-12 pr-12"
                  placeholder="Min. 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="luxury-input pl-12 pr-12"
                  placeholder="Repeat new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={savingPassword}
                className="flex items-center gap-2 px-6 py-3 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
              >
                <KeyRound className="w-4 h-4" />
                {savingPassword ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
